import { Style, Variant, Visibility } from '.'
import { PreHeader } from './PreHeader'
import { HeaderNavigation } from './navigation'
import Search from './search'
import { useHeaderContent } from './utils/useHeaderContent'
import { HeaderDiamondsMenu } from '@components/Page/Header/DiamondsMenu'
import { HeaderDropdown } from '@components/Page/Header/Dropdown'
import { HeaderHouseOfGassanMenu } from '@components/Page/Header/HouseOfGassanMenu'
import MobileNav from '@components/Page/Header/mobile-nav'
import { HeaderShopMenu } from '@components/Page/Header/shop-menu'
import { NavigationContent } from '@lib/kontent/navigation-helpers/normalize-navigation-content'
import { AnimatePresence } from 'framer-motion'
import { FC, useRef } from 'react'
import { cn } from '@lib/cn'

export type NavActiveOptions =
  | 'shop'
  | 'diamonds'
  | 'search'
  | 'house-of-gassan'
  | 'mobile-nav'
  | null

interface Props {
  variant: Variant
  visibility: Visibility
  headerStyle: Style
  content: NavigationContent
}

export const HeaderContent: FC<Props> = ({ variant, visibility, headerStyle, content }) => {
  const ref = useRef<HTMLElement>(null)
  const { active, handleSetActive, menuIsActive } = useHeaderContent({ ref })

  return (
    <>
      <div
        className={cn(
          'duration-400 pointer-events-none absolute left-0 top-0 z-[4] h-16 w-full transition-opacity ease-in-out',
          'lg:h-44',
          'xl:h-[200px]',
        )}
      ></div>
      <PreHeader />
      <header
        className={cn(
          'absolute top-0 z-[22] flex h-12 w-full items-center justify-center',
          'md:h-[72px]',
          'lg:top-10 lg:flex lg:h-[140px]',
          variant === 'absolute' && 'border-b border-shade-200 bg-white',
          variant === 'fixed' &&
            'fixed !top-0 z-[22] -translate-y-full border-b border-shade-200 bg-white',
          variant === 'fixed' && headerStyle === 'light' && 'fill-white text-white',
          variant === 'fixed' && headerStyle === 'dark' && 'fill-shade-800 text-shade-800',
          visibility === 'visible' && 'translate-y-0 transition-transform duration-500 ease-in-out',
          visibility === 'hidden' &&
            '-translate-y-full transition-transform duration-500 ease-in-out',
          active !== null && 'fixed z-[40] border-b border-tabaccoBrown/10 bg-pampas',
        )}
        ref={ref}
        data-variant={variant}
        data-header-style={headerStyle}
        data-visibility={visibility}
        data-has-dropdown={active !== null ? '' : undefined}
      >
        <HeaderNavigation onClickItem={handleSetActive} active={active} />
      </header>
      <AnimatePresence>
        <HeaderDropdown
          menuIsActive={menuIsActive}
          onRequestClose={() => handleSetActive(null)}
          variant={active === 'search' ? 'fullscreen-on-mobile' : 'default'}
        >
          <HeaderShopMenu isVisible={active === 'shop'} content={content} />
          <HeaderDiamondsMenu isVisible={active === 'diamonds'} {...content.diamonds} />
          <HeaderHouseOfGassanMenu
            isVisible={active === 'house-of-gassan'}
            columns={content.houseOfGassan}
          />
          {active === 'search' && <Search onRequestClose={() => handleSetActive(null)} />}
          <MobileNav isVisible={active === 'mobile-nav'} content={content} />
        </HeaderDropdown>
      </AnimatePresence>
    </>
  )
}
